import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useConfirm } from 'material-ui-confirm';

import {
  Add as AddIcon,
  DeleteOutlineOutlined as DeleteOutlineOutlinedIcon,
  EditOutlined as EditOutlinedIcon,
} from '@mui/icons-material';

import { deleteCampaign } from 'store/actions/campaigns';
import { setSnackbarError, setSnackbarSuccess } from 'store/actions/global';
import { campaignsSelectors } from 'store/selectors/campaigns';

import { showFirstErrorMessage } from 'utils/helpers';

import TopCard from 'common/containers/TopCard';

import RenameCampaignDialog from '../RenameCampaignDialog';

const formatDate = date => {
  const options = {
    day: '2-digit',
    hour: '2-digit',
    hour12: true,
    minute: '2-digit',
    month: 'long',
    second: '2-digit',
    year: 'numeric',
  };

  return new Date(date).toLocaleString('en-US', options);
};

const CampaignTopCard = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const confirm = useConfirm();
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);

  const { campaignUuid, organizationUuid } = useParams();

  const { campaignDetails, isPending } = useSelector(state => ({
    campaignDetails: campaignsSelectors.getCampaignDetails(state),
    isPending: campaignsSelectors.isPending(state),
  }));

  const { canDelete, createdAt, createdBy, name } = campaignDetails;

  const handleDeleteCampaign = () => {
    confirm({
      confirmationText: 'Delete',
      description: 'You won`t be able to undo that',
      title: 'Delete the Campaign?',
    })
      .then(() => dispatch(deleteCampaign(campaignUuid)))
      .then(() => dispatch(setSnackbarSuccess({ message: 'Campaign has been removed' })))
      .then(() => history.push(`/${organizationUuid}/campaigns/`))
      .catch(error => {
        if (error) {
          dispatch(
            setSnackbarError({
              message: showFirstErrorMessage(error),
            })
          );
        }
      });
  };

  const handleAddActionCenter = () => {
    history.push(`/${organizationUuid}/create-action-center/${campaignUuid}`);
  };

  const topCardData = useCallback(() => {
    const actionButtons = [
      {
        color: 'grey',
        name: 'Rename Campaign',
        onClick: () => setIsRenameDialogOpen(true),
        startIcon: <EditOutlinedIcon />,
      },
      {
        color: 'grey',
        disabled: !canDelete,
        name: 'Delete',
        onClick: handleDeleteCampaign,
        startIcon: <DeleteOutlineOutlinedIcon />,
        tooltipText: !canDelete && 'Stop this campaign`s action centers before you can delete it.',
      },
      {
        color: 'primary',
        name: 'New Action Center',
        onClick: handleAddActionCenter,
        startIcon: <AddIcon />,
        variant: 'contained',
      },
    ];

    return {
      actionButtons,
      backButtonLink: `/${organizationUuid}/campaigns`,
      dateInfo: `${formatDate(createdAt)} ${createdBy && `by ${createdBy.firstName} ${createdBy.lastName}`}`,
      details: [],
      title: name || '',
    };
  }, [canDelete, createdAt, name]);

  return (
    <>
      <TopCard {...topCardData()} />
      <RenameCampaignDialog
        campaignName={name}
        isPending={isPending}
        isRenameDialogOpen={isRenameDialogOpen}
        setIsRenameDialogOpen={setIsRenameDialogOpen}
      />
    </>
  );
};

export default CampaignTopCard;
